import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid2";
import TripRateCalculationViewModel from "../ViewModels/TripRateCalculationViewModel";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { useState, useContext } from "react";
import MenuItem from "@mui/material/MenuItem";
import TripRateDay from "../ViewModels/TripRateDay";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { APIContext } from "../../Contexts/APIContext";


interface IProps {
    dialogOpen: boolean;
    handleClose: () => void;
    tripRate: TripRateCalculationViewModel;
}

export default function KeyPeriodDialog({ dialogOpen, handleClose, tripRate }: IProps) {


    const [startTime, setStartTime] = useState("12:00");
    const [endTime, setEndTime] = useState("13:00");
    const [userDefinedChecked, setUserDefinedChecked] = useState(false);
    const [title, setTitle] = useState("");
    const [userDefinedPeriods, setUserDefinedPeriods] = useState<{ period: string; arrivalRate: number; departureRate: number; totalRate: number }[]>([]);
    const { showMessage, postPDF } = useContext(APIContext);
    const getFilteredPeriods = () => {
        const list = tripRate.totals ?? [];
        return (list as TripRateDay[])
            .filter((time) => time.numberOfDays != null && time.numberOfDays > 0)
            .map((time) => (
                <MenuItem key={time.period?.split("-")[0]} value={time.period?.split("-")[0]}>
                    {time.period?.split("-")[0]}
                </MenuItem>
            ));
    };

    const getUserDefinedPeriod = () => {
        const periodList = tripRate.totals.map(t => t.period);

        const startIndex = periodList.findIndex(p => p?.startsWith(startTime));
        const endIndex = periodList.findIndex(p => p?.endsWith(endTime));

        if (startIndex === -1 || endIndex === -1 || endIndex < startIndex) {

            showMessage("error", "Please select a valid period range.");
            return;
        }

        const selectedPeriods = periodList.slice(startIndex, endIndex + 1);

        const sumRates = (data: TripRateDay[]) =>
            data
                .filter(d => selectedPeriods.includes(d.period))
                .reduce((sum, curr) => sum + (curr.tripRate ?? 0), 0);

        const periodLabel = `${startTime}-${endTime}`;

        setUserDefinedPeriods([
            {
                period: periodLabel,
                arrivalRate: sumRates(tripRate.arrivals),
                departureRate: sumRates(tripRate.departures),
                totalRate: sumRates(tripRate.totals)
            }
        ]);
    };
    const exportPDF = () => {
        postPDF("PDFGenerator", `GetKeyPeriodPDF?title=${title}&userDefined=${userDefinedChecked}&startTime=${startTime}&endTime=${endTime}`, "KeyPeriodTripRates", false, tripRate);
    }




    return (
        <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth >
            <DialogTitle>Key Period Trip Rates</DialogTitle>
            <DialogContent>
                <Grid container size={12}>
                    <Grid size={12}>
                        <TextField label={"Title"} value={title} onChange={e => setTitle(e.target.value)} />
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="body2">Set Period 1: 08:00-09:00</Typography>
                        <Typography variant="body2">Set Period 2: 17:00-18:00</Typography>
                        <FormControlLabel label="User defined period" control={<Checkbox checked={userDefinedChecked} onChange={() => setUserDefinedChecked(!userDefinedChecked)} />} />
                    </Grid>
                    <Grid size={6}>
                        {userDefinedChecked &&
                            <>
                                <Typography variant="body2">User Defined Period</Typography>
                                <TextField select label="Start" value={startTime} onChange={(e) => setStartTime(e.target.value)} fullWidth margin="dense">{getFilteredPeriods()}</TextField>
                                <TextField select label="End" value={endTime} onChange={(e) => setEndTime(e.target.value)} fullWidth margin="dense">{getFilteredPeriods()}</TextField>
                                <Button onClick={() => getUserDefinedPeriod()}>Add Period</Button>
                            </>
                        }
                    </Grid>
                    <Grid size={12}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell rowSpan={2}>
                                            <Typography variant={"body2"}>Period</Typography>
                                        </TableCell>
                                        <TableCell colSpan={3} align="center">
                                            <Typography variant={"body2"}>Trips per 100 sqm GFA</Typography>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><Typography variant={"body2"}>Inbound</Typography></TableCell>
                                        <TableCell><Typography variant={"body2"}>Outbound</Typography></TableCell>
                                        <TableCell><Typography variant={"body2"}>Total</Typography></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {/* Default Fixed Periods */}
                                    {["08:00-09:00", "17:00-18:00"].map((period) => {
                                        const arrival = tripRate.arrivals.find(x => x.period === period);
                                        const departure = tripRate.departures.find(x => x.period === period);
                                        const total = tripRate.totals.find(x => x.period === period);

                                        return (
                                            <TableRow key={period}>
                                                <TableCell><Typography variant={"body2"}>{period}</Typography></TableCell>
                                                <TableCell><Typography variant={"body2"}>{arrival?.tripRate?.toFixed(3) ?? "-"}</Typography></TableCell>
                                                <TableCell><Typography variant={"body2"}>{departure?.tripRate?.toFixed(3) ?? "-"}</Typography></TableCell>
                                                <TableCell><Typography variant={"body2"}>{total?.tripRate?.toFixed(3) ?? "-"}</Typography></TableCell>
                                            </TableRow>
                                        );
                                    })}

                                    {userDefinedChecked && userDefinedPeriods.map((period, index) => (
                                        <TableRow key={`user-${index}`}>
                                            <TableCell><Typography variant={"body2"}>{period.period}</Typography></TableCell>
                                            <TableCell><Typography variant={"body2"}>{period.arrivalRate.toFixed(3)}</Typography></TableCell>
                                            <TableCell><Typography variant={"body2"}>{period.departureRate.toFixed(3)}</Typography></TableCell>
                                            <TableCell><Typography variant={"body2"}>{period.totalRate.toFixed(3)}</Typography></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                    </Grid>

                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={exportPDF }>View as PDF</Button>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>

    );
}